/**
 * Manual order due dates (Phase 104, BLU-1450, D-14).
 *
 * A due date is a plain calendar date (`YYYY-MM-DD`, prod_orders.due_date is a
 * Postgres `date`), never a timestamp, so the pill colour must not move with
 * the viewer's timezone. Both inputs are date-only strings; the arithmetic is
 * whole UTC days between them, so no clock or offset is involved. The caller
 * passes `today` as its own local calendar date (the browser's, 104-19).
 *
 *   - 'overdue_or_soon' (red):   due <= today + 2 days (overdue included)
 *   - 'this_week'       (amber): due <= today + 7 days
 *   - 'later':                   anything after that
 *
 * A missing or malformed date returns null (no pill).
 */
/** Red at or before today + this many days (overdue included). */
export const DUE_SOON_DAYS = 2;
/** Amber at or before today + this many days. */
export const DUE_THIS_WEEK_DAYS = 7;
const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const MS_PER_DAY = 86_400_000;
/** Whole days since the epoch for a `YYYY-MM-DD` string, or null when malformed. */
function epochDay(date) {
    const m = DATE_ONLY_RE.exec(date);
    if (!m)
        return null;
    const year = Number(m[1]);
    const month = Number(m[2]);
    const day = Number(m[3]);
    const ms = Date.UTC(year, month - 1, day);
    // Reject roll-overs such as 2026-02-31 (Date.UTC would quietly make it March).
    const check = new Date(ms);
    if (check.getUTCFullYear() !== year || check.getUTCMonth() !== month - 1 || check.getUTCDate() !== day) {
        return null;
    }
    return Math.round(ms / MS_PER_DAY);
}
/**
 * How urgent a due date is, relative to `today` (both `YYYY-MM-DD`).
 * Returns null when there is no due date or either date is malformed.
 */
export function dueUrgency(dueDate, today) {
    if (!dueDate)
        return null;
    const due = epochDay(dueDate);
    const now = epochDay(today);
    if (due === null || now === null)
        return null;
    const daysLeft = due - now;
    if (daysLeft <= DUE_SOON_DAYS)
        return 'overdue_or_soon';
    if (daysLeft <= DUE_THIS_WEEK_DAYS)
        return 'this_week';
    return 'later';
}
